import { injectAds, injectAdsBySeconds } from './breakResolver'
import { subtractSegments } from './portions'
import type { Channel, QueueItem } from './types'

export interface BuildChannelQueueOptions {
  shuffle: boolean
  adBumpersEnabled: boolean
  random?: () => number
}

/** A channel's PROGRAMS as queue items, in natural order — the listed shows. Usually YouTube videos,
 *  but a channel may author its programs differently: as bump SCENES (`adReel`, the ad channel) or as a
 *  single interactive COMPONENT (`kind:'component'`, e.g. HIDEO). Each is a first-class program (role
 *  'program'), so it gets a guide slot and a live schedule for free — no channel-level bypass. */
export function channelProgramItems(ch: Channel): QueueItem[] {
  if (ch.adReel && ch.adReel.length) return adReelItems(ch)
  // an interactive-component channel IS one always-on program (Infinity duration → it owns the timeline).
  if (ch.kind === 'component' && ch.component) {
    return [{ kind: 'component', role: 'program', component: ch.component, title: ch.name }]
  }

  const baked: QueueItem[] = (ch.programs || [])
    .filter((p) => p.embeddable)
    .map((p) => {
      // derive the kept content portions by cutting sponsor ranges; omit when whole-video.
      const portions = subtractSegments(p.durationSeconds, p.skipSegments || [])
      const item: QueueItem = { kind: 'video', videoId: p.videoId, title: p.title, durationSeconds: p.durationSeconds }
      return portions.length ? { ...item, portions } : item
    })
  return baked.length
    ? baked
    : (ch.videos || []).map((videoId) => ({ kind: 'video', videoId, title: '' }))
}

export function shuffleItems<T>(items: T[], random: () => number = Math.random): T[] {
  const out = items.slice()
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1))
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out
}

/** The ad channel's programs: one SCENE program (role 'program') per scene name in `adReel`. They are
 *  listed shows — guide-navigable, live-schedulable — not injected breaks. Titles derive from the
 *  scene name for the guide/OSD. */
function adReelItems(ch: Channel): QueueItem[] {
  return (ch.adReel ?? []).map((scene) => ({
    kind: 'scene',
    role: 'program',
    scene,
    params: {},
    title: scene.replace(/^ad-/, '').replace(/-/g, ' ').toUpperCase(),
  }))
}

export function buildChannelQueue(
  ch: Channel,
  { shuffle, adBumpersEnabled, random = Math.random }: BuildChannelQueueOptions,
): QueueItem[] {
  const programs = channelProgramItems(ch)
  const ordered = shuffle ? shuffleItems(programs, random) : programs
  // weave interstitial breaks between programs only if the channel configures them — an ad channel
  // (no breaks.ads) just plays its programs back-to-back, which is the same general path.
  return buildQueueFromVideos(ordered, ch, adBumpersEnabled, random)
}

export function buildQueueFromVideos(
  videos: QueueItem[],
  ch: Channel,
  adBumpersEnabled: boolean,
  random: () => number = Math.random,
): QueueItem[] {
  // seconds-based cadence (splits items mid-content) when configured, else the legacy count-based one.
  return ch.breaks?.ads?.everySeconds
    ? injectAdsBySeconds(videos, ch, adBumpersEnabled, random)
    : injectAds(videos, ch, adBumpersEnabled, random)
}

/** The player view of the queue: video ids, or null for ad slots. */
export function queueEntries(queue: QueueItem[]): (string | null)[] {
  return queue.map((q) => (q.kind === 'video' ? q.videoId : null))
}

/** The video id of the next VIDEO item after `index` (skipping ads), cycling the loop. Used to
 *  pre-buffer the upcoming video into the standby frame. Null for an empty queue. */
export function nextVideoIdAfter(queue: QueueItem[], index: number): string | null {
  const n = queue.length
  if (!n) return null
  for (let k = 1; k <= n; k++) {
    const it = queue[(((index + k) % n) + n) % n]
    if (it && it.kind === 'video') return it.videoId
  }
  return null
}
